import { useEffect, useId, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { Loader2 } from "lucide-react";
import { cn } from "@/modern/utils";

const ELEVATED_Z_INDEX = "z-[calc(var(--swivu-bryntum-z-index)+30)]";

export interface NamePromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  fieldLabel: string;
  placeholder?: string;
  initialValue: string;
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;
  /** Stack above Bryntum overlays and dropdown menus */
  elevated?: boolean;
  onConfirm: (value: string) => void | Promise<void>;
}

export const NamePromptModal = ({
  open,
  onOpenChange,
  title,
  description,
  fieldLabel,
  placeholder,
  initialValue,
  submitLabel = "Save",
  submittingLabel = "Saving…",
  cancelLabel = "Cancel",
  elevated = true,
  onConfirm,
}: NamePromptModalProps) => {
  const inputId = useId();
  const [value, setValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(initialValue.trim());
      setIsSubmitting(false);
    }
  }, [open, initialValue]);

  const trimmedValue = value.trim();
  const canSubmit = trimmedValue.length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await onConfirm(trimmedValue);
      onOpenChange(false);
    } catch {
      // Parent may show toast; keep modal open for retry
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isSubmitting) onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn("gap-0 p-0 sm:max-w-md", elevated && ELEVATED_Z_INDEX)}
        dialogOverlayClassName={elevated ? ELEVATED_Z_INDEX : undefined}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="pr-10">
          <DialogTitle>{title}</DialogTitle>
          {description != null && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-2 p-swivu-base">
          <Label htmlFor={inputId} className="text-sm font-medium">
            {fieldLabel}
          </Label>
          <Input
            id={inputId}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            disabled={isSubmitting}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleSubmit();
              }
            }}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className="gap-1.5"
          >
            {isSubmitting && <Loader2 className="size-3.5 shrink-0 animate-spin" />}
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
